import { Box, Typography, Button } from "@mui/material";
import { Add } from "@mui/icons-material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import React, { useEffect, useState, useRef } from "react";
import { defaultParams } from "../../App";
import AcrInputs from "./AcrInputs";
import FormWrapper from "./FormWrapper";
import MortgageTypeInput from "./MortgageTypeInput";
import RepaymentInputs from "./RepaymentInputs";
import SavingsInputs from "./SavingsInputs";
import MonthlyBudgetEntry from "./MonthlyBudgetEntry";
import { CurrencyField } from "./CurrencyField";
import {
  SimulationFormProps,
  SimulationFormInputs,
  SimulationFormParams,
} from "../../types/forms";
import SubmitButton from "./SubmitButton";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

const demoParams: SimulationFormInputs = {
  principal: 250000,
  interestOnly: false,
  termYears: 25,
  interestRate: 4.5,
  desiredMonthlyPayment: undefined,
  additionalCapitalRepayments: true,
  dealLength: 24,
  maxAffordable: 0,
  savingsInterestRate: 3.5,
  saveAtStart: false,
  additionalCapitalRepaymentPercentageLimit: 10,
  reduceTermOnAdditionalCapitalRepayment: false,
  savingsAccounts: [],
};

export default function AcrSimulationForm({
  model,
  onSubmit,
  onCancel,
  isEditing,
  editingIndex,
}: SimulationFormProps) {
  const [localIndices, setLocalIndices] = useState<number[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const hasInitialized = useRef<boolean>(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localIndices.findIndex(
        (idx) => `budget-${idx}` === active.id,
      );
      const newIndex = localIndices.findIndex(
        (idx) => `budget-${idx}` === over.id,
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        model.moveSimulation(oldIndex, newIndex);
      }
    }
  };

  useEffect(() => {
    if (!isEditing && !hasInitialized.current) {
      hasInitialized.current = true;
      const newSim1: SimulationFormParams = {
        inputs: {
          ...defaultParams,
          additionalCapitalRepayments: true,
          maxAffordable: 0,
        },
        updated: true,
      };
      const newSim2: SimulationFormParams = {
        inputs: {
          ...defaultParams,
          additionalCapitalRepayments: true,
          maxAffordable: 0,
        },
        updated: true,
      };
      model.addSimulation(newSim1);
      model.addSimulation(newSim2);
      setLocalIndices([0, 1]);
      setIsInitialized(true);
    } else if (isEditing && !hasInitialized.current) {
      const existingIndices = model.simulations.map((_, i) => i);
      setLocalIndices(existingIndices);
      setIsInitialized(true);
      hasInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    if (!isEditing && localIndices.length > 0) {
      [...localIndices].reverse().forEach((idx) => {
        model.removeSimulation(idx);
      });
    }
    onCancel();
  };

  const handleAddSimulation = () => {
    const params = model.simulations[localIndices[0]]?.inputs || defaultParams;
    const newSim: SimulationFormParams = {
      inputs: {
        ...params,
        additionalCapitalRepayments: true,
        maxAffordable: 0,
      },
      updated: true,
    };
    model.addSimulation(newSim);
    setLocalIndices([...localIndices, model.simulations.length]);
  };

  const handleRemoveSimulation = (indexInLocalArray: number) => {
    if (localIndices.length <= 2) return;

    const actualIndex = localIndices[indexInLocalArray];
    model.removeSimulation(actualIndex);

    const newLocalIndices = localIndices
      .filter((_, i) => i !== indexInLocalArray)
      .map((idx) => (idx > actualIndex ? idx - 1 : idx));

    setLocalIndices(newLocalIndices);
  };

  const handleDemo = () => {
    if (localIndices.length > 0) {
      [...localIndices].reverse().forEach((idx) => {
        model.removeSimulation(idx);
      });
    }

    const newSim1: SimulationFormParams = {
      inputs: { ...demoParams, maxAffordable: 1800 },
      updated: true,
    };
    const newSim2: SimulationFormParams = {
      inputs: { ...demoParams, maxAffordable: 2000 },
      updated: true,
    };
    model.addSimulation(newSim1);
    model.addSimulation(newSim2);
    setLocalIndices([
      model.simulations.length - 2,
      model.simulations.length - 1,
    ]);
  };

  if (!isInitialized) {
    return null;
  }

  if (localIndices == null) {
    return null;
  }

  return (
    <FormWrapper onSubmit={onSubmit}>
      {(error) => (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {isEditing ? "Update Simulations" : "Create Simulations"}
            </Typography>
            <Button
              startIcon={<AutoFixHighIcon />}
              onClick={handleDemo}
              variant="outlined"
              size="small"
            >
              Demo
            </Button>
          </Box>

          <CurrencyField
            label="Mortgage Debt"
            value={model.simulations[localIndices[0]]?.inputs.principal}
            onChange={(val) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, { principal: val });
              });
            }}
            fullWidth
            margin="normal"
            required
            greaterThanZero
          />

          <MortgageTypeInput
            interestOnly={
              model.simulations[localIndices[0]]?.inputs.interestOnly
            }
            interestRate={
              model.simulations[localIndices[0]]?.inputs.interestRate
            }
            desiredMonthlyPayment={
              model.simulations[localIndices[0]]?.inputs.desiredMonthlyPayment
            }
            onChange={(updates: Partial<SimulationFormInputs>) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, updates);
              });
            }}
          />

          <RepaymentInputs
            interestOnly={
              model.simulations[localIndices[0]]?.inputs.interestOnly
            }
            termYears={model.simulations[localIndices[0]]?.inputs.termYears}
            interestRate={
              model.simulations[localIndices[0]]?.inputs.interestRate
            }
            desiredMonthlyPayment={
              model.simulations[localIndices[0]]?.inputs.desiredMonthlyPayment
            }
            onChange={(updates: Partial<SimulationFormInputs>) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, updates);
              });
            }}
          />

          <AcrInputs
            additionalCapitalRepayments={true}
            dealLength={model.simulations[localIndices[0]]?.inputs.dealLength}
            maxAffordable={
              model.simulations[localIndices[0]]?.inputs.maxAffordable
            }
            savingsInterestRate={
              model.simulations[localIndices[0]]?.inputs.savingsInterestRate
            }
            saveAtStart={model.simulations[localIndices[0]]?.inputs.saveAtStart}
            additionalCapitalRepaymentPercentageLimit={
              model.simulations[localIndices[0]]?.inputs
                .additionalCapitalRepaymentPercentageLimit
            }
            reduceTermOnAdditionalCapitalRepayment={
              model.simulations[localIndices[0]]?.inputs
                .reduceTermOnAdditionalCapitalRepayment
            }
            skipAdditionalCapitalRepayments={
              model.simulations[localIndices[0]]?.inputs
                .skipAdditionalCapitalRepayments
            }
            interestOnly={
              model.simulations[localIndices[0]]?.inputs.interestOnly
            }
            onChange={(updates: Partial<SimulationFormInputs>) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, updates);
              });
            }}
            alwaysExpanded={true}
            hideMonthlyBudget={true}
            hideSkipAcr={true}
          />

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Monthly Budget Comparison
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add different monthly budget amounts to compare how they affect
              your mortgage. Each budget creates a separate simulation.
            </Typography>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={localIndices.map((idx) => `budget-${idx}`)}
                strategy={verticalListSortingStrategy}
              >
                {localIndices.map((actualIndex, i) => {
                  const formInputs = model.simulations[actualIndex]?.inputs || {
                    ...defaultParams,
                    maxAffordable: 0,
                  };
                  return (
                    <MonthlyBudgetEntry
                      key={actualIndex}
                      id={`budget-${actualIndex}`}
                      index={i}
                      value={formInputs.maxAffordable}
                      onChange={(val) =>
                        model.updateSimulation(
                          actualIndex,
                          {},
                          { maxAffordable: val },
                        )
                      }
                      onRemove={() => handleRemoveSimulation(i)}
                      canRemove={localIndices.length > 2}
                      autoFocus={editingIndex === actualIndex}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
            <Button
              startIcon={<Add />}
              onClick={handleAddSimulation}
              variant="outlined"
              fullWidth
              sx={{ mt: 1 }}
            >
              Add Simulation
            </Button>
          </Box>

          <SavingsInputs
            accounts={
              model.simulations[localIndices[0]]?.inputs.savingsAccounts
            }
            onChange={(updates: Partial<SimulationFormInputs>) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, updates);
              });
            }}
          />

          <SubmitButton
            isEditing={isEditing}
            multipleSimulations={true}
            error={error}
            onCancel={handleCancel}
          />
        </>
      )}
    </FormWrapper>
  );
}
